import { resolveMonthlyFeePct } from './xirrFee';
import {
  resolveFirstMarketingMonth,
  resolvePlanFirstMarketingMonth,
} from './resolveDealIrrModelInputs';

export function formatDealIrrMonthInputValue(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

export function parseDealIrrMonthInputValue(value) {
  const match = String(value || '').trim().match(/^(\d{4})-(\d{2})$/);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  if (!Number.isFinite(year) || month < 0 || month > 11) {
    return null;
  }
  return new Date(year, month, 1);
}

export function resolveDealIrrTableFirstMarketingMonth(state) {
  return resolveFirstMarketingMonth(state);
}

export function isDealIrrFirstMarketingMonthOverridden(state) {
  const explicit = state?.firstMarketingMonth;
  if (!(explicit instanceof Date) || Number.isNaN(explicit.getTime())) {
    return false;
  }
  const planMonth = resolvePlanFirstMarketingMonth(state);
  return (
    explicit.getFullYear() !== planMonth.getFullYear() ||
    explicit.getMonth() !== planMonth.getMonth()
  );
}

export function updateDealIrrTableFirstMarketingMonth(setState, value) {
  const parsed = parseDealIrrMonthInputValue(value);
  if (!parsed) {
    return;
  }
  setState((currentState) => ({
    ...currentState,
    planStartYear: parsed.getFullYear(),
    planStartMonth: parsed.getMonth(),
    firstMarketingMonth: parsed,
    firstPlanMonthIsCurrentMonth: false,
  }));
}

export function resetDealIrrTableFirstMarketingMonth(setState) {
  setState((currentState) => ({
    ...currentState,
    firstMarketingMonth: null,
  }));
}

export function updateDealIrrTableMarketing(setState, index, rawValue) {
  const val = Math.max(0, Number(rawValue) || 0);
  setState((s) => {
    const marketing = [...(s.marketing || [])];
    marketing[index] = val;
    const next = { ...s, marketing };
    if (s.marketingPlanManualMode) {
      const fundingPct = [...(s.fundingPct || [])];
      const planDrawAmount = [...(s.planDrawAmount ?? [])];
      while (planDrawAmount.length <= index) {
        planDrawAmount.push(null);
      }
      const pct = Number(fundingPct[index]) || 0;
      planDrawAmount[index] =
        val > 0 && pct > 0 ? Math.min(Math.round((val * pct) / 100), val) : null;
      next.fundingPct = fundingPct;
      next.planDrawAmount = planDrawAmount;
    }
    return next;
  });
}

export function seedMonthlyIrrPct(modelState) {
  const defaultPct = resolveMonthlyFeePct(modelState.feeType, modelState.feePct);
  const existing = modelState?.monthlyIrrPct || [];
  return (modelState?.marketing || []).map((_, index) => {
    const stored = existing[index];
    if (stored != null && stored !== '' && Number.isFinite(Number(stored))) {
      return Number(stored);
    }
    return defaultPct;
  });
}

export function updateDealIrrTableMonthlyIrr(setState, index, rawValue) {
  const val = Math.max(0, Number(rawValue) || 0);
  setState((s) => {
    const planLength = s.marketing?.length ?? 0;
    if (index < 0 || index >= planLength) {
      return s;
    }
    const defaultPct = resolveMonthlyFeePct(s.feeType, s.feePct);
    const monthlyIrrPct = [...(s.monthlyIrrPct || [])];
    while (monthlyIrrPct.length <= index) {
      monthlyIrrPct.push(defaultPct);
    }
    monthlyIrrPct[index] = val;
    return { ...s, monthlyIrrPct };
  });
}

export function updateDealIrrTableRoas(setState, index, rawValue) {
  const val = Math.max(0, Number(rawValue) || 0);
  setState((s) => {
    const roas = [...(s.roas || [])];
    while (roas.length <= index) {
      roas.push(0);
    }
    roas[index] = val;
    return { ...s, roas };
  });
}

export function resolveDealIrrTableRoasDisplay(state, index, modelValue) {
  const stored = state?.roas?.[index];
  if (stored != null && stored !== '') {
    return Number(stored);
  }
  return Number.isFinite(Number(modelValue)) ? Number(modelValue) * 100 : 0;
}

export function resolveDealIrrTableMonthlyIrrDisplay(state, index) {
  const stored = state?.monthlyIrrPct?.[index];
  if (stored != null && stored !== '') {
    return Number(stored);
  }
  return resolveMonthlyFeePct(state?.feeType, state?.feePct);
}
